import {Injectable} from '@angular/core';
import {UserInterface} from '../model/user.interface';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import {environment} from '../../environments/environment';
import {AbstractHttpService} from './abstract-http-service';
import {defer, firstValueFrom, Observable, of} from 'rxjs';
import {catchError, finalize, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {IS_PUBLIC_ENDPOINT} from "../inteceptors/auth.inteceptor";
import {MpopUserModel} from "../model/mpop-user.model";
import {MpopFeatureModel} from '../model/mpop-feature.model';

@Injectable({
  providedIn: 'root'
})
export class UserService extends AbstractHttpService {
  private static readonly featureSnapshotMaxAgeMilliseconds = 600000;
  private static readonly redisFeatureFlagCacheFeatureName = 'redisFeatureFlagCache';
  private user;
  private mpopFeatureSnapshot: Map<string, boolean> | null = null;
  private mpopFeatureSnapshotLoadedAt: number | null = null;
  private mpopFeatureRequest: Observable<Map<string, boolean>> | null = null;
  private redisFeatureFlagCacheEnabled: boolean | null = null;
  private redisFeatureFlagCacheLoadedAt: number | null = null;
  private redisFeatureFlagCacheRequest: Observable<boolean> | null = null;

  defaultOptions = {
    headers: this.contentTypeJsonHeaders,
    withHeaders: true
  };

  publicEndpointOptions = {
    headers: this.acceptJsonHeaders,
    withHeaders: true,
    context: new HttpContext().set(IS_PUBLIC_ENDPOINT, true)
  }

  postOptions = {
    headers: this.contentTypeJsonHeaders,
    withCredentials: true
  };

  patchOptions = {
    headers: this.contentTypeJsonHeadersForPatch,
    withCredentials: true
  };

  constructor(private httpClient: HttpClient) {
    super();
  }

  public async getUserPromise(): Promise<UserInterface> {
    return await firstValueFrom(this.getUser());
  }

  public getUser(): Observable<UserInterface> {
    return this.httpClient.get<UserInterface>(
      `//${environment.autoDomain}:${environment.autoPort}/api/current_user_permissions`,
      this.defaultOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  public getMpopUsers(): Observable<MpopUserModel[]> {
    return this.httpClient.get<MpopUserModel[]>(
      `//${environment.autoDomain}:${environment.autoPort}/api/mpop-users`,
      this.defaultOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Method to get the user logins
   *
   * @param userId
   * @return Observable<any>
   */
  public getRecentLogins(userId: number): Observable<any> {
    return this.httpClient.get(
      `//${environment.autoDomain}:${environment.autoPort}/api/recent-logins?userId=${userId}`,
      this.defaultOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  public getMpopFeature(featureName: string): Observable<MpopFeatureModel[]>
  {

    return defer(() => this.isRedisFeatureFlagCacheEnabled()).pipe(
      switchMap((isCacheEnabled) => this.getMpopFeatureByCacheState(featureName, isCacheEnabled))
    );
  }

  /**
   * Returns all mpop features
   */
  public getAllMpopFeatures(): Observable<MpopFeatureModel[]>
  {

    return defer(() => this.isRedisFeatureFlagCacheEnabled()).pipe(
      switchMap((isCacheEnabled) => this.getAllMpopFeaturesByCacheState(isCacheEnabled))
    );
  }

  /**
   * Gets feature by cache state
   * @param featureName
   * @param isCacheEnabled
   * @private
   */
  private getMpopFeatureByCacheState(
    featureName: string,
    isCacheEnabled: boolean
  ): Observable<MpopFeatureModel[]>
  {
    let featureRequest = this.loadLegacyMpopFeature(featureName);

    if (isCacheEnabled) {
      featureRequest = this.getMpopFeatureSnapshot().pipe(
        map((featureSnapshot) => {
          const featureEnabled = featureSnapshot.get(featureName) === true;

          return [new MpopFeatureModel(featureName, featureEnabled)];
        })
      );
    }

    return featureRequest;
  }

  /**
   * Returns all mpop features
   */
  private getAllMpopFeaturesByCacheState(isCacheEnabled: boolean): Observable<MpopFeatureModel[]>
  {
    let featureRequest = this.loadLegacyMpopFeatures();

    if (isCacheEnabled) {
      featureRequest = this.getMpopFeatureSnapshot().pipe(
        map((featureSnapshot) => this.buildMpopFeatureResults(featureSnapshot))
      );
    }

    return featureRequest;
  }

  /**
   * Makes call for redisFeatureFlag feature
   * @param featureName
   * @private
   */
  private loadLegacyMpopFeature(featureName: string): Observable<MpopFeatureModel[]>
  {
    const requestOptions = {
      ...this.publicEndpointOptions,
      params: new HttpParams().set('name', featureName)
    };

    return this.httpClient.get<MpopFeatureModel[]>(
      `//${environment.autoDomain}:${environment.autoPort}/api/mpop-feature`,
      requestOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Loads mpop features the old way
   * @private
   */
  private loadLegacyMpopFeatures(): Observable<MpopFeatureModel[]>
  {

    return this.httpClient.get<MpopFeatureModel[]>(
      `//${environment.autoDomain}:${environment.autoPort}/api/mpop-feature`,
      this.publicEndpointOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Checks if redisFeatureFlagCache is enabled
   * @private
   */
  private isRedisFeatureFlagCacheEnabled(): Observable<boolean>
  {
    const isCacheEnabled = this.redisFeatureFlagCacheEnabled;
    let cacheRequest = this.redisFeatureFlagCacheRequest;

    if (isCacheEnabled !== null && this.isRedisFeatureFlagCacheDecisionCurrent()) {
      cacheRequest = of(isCacheEnabled);
    }

    if (cacheRequest === null) {
      cacheRequest = this.loadRedisFeatureFlagCacheDecision();
      this.redisFeatureFlagCacheRequest = cacheRequest;
    }

    return cacheRequest;
  }

  /**
   * @private
   */
  private loadRedisFeatureFlagCacheDecision(): Observable<boolean>
  {
    const requestOptions = {
      ...this.publicEndpointOptions,
      params: new HttpParams().set(
        'name',
        UserService.redisFeatureFlagCacheFeatureName
      )
    };

    return this.httpClient.get<unknown>(
      `//${environment.autoDomain}:${environment.autoPort}/api/mpop-feature`,
      requestOptions
    ).pipe(
      map((response) => this.getRedisFeatureFlagCacheDecision(response)),
      tap((isCacheEnabled) => this.storeRedisFeatureFlagCacheDecision(isCacheEnabled)),
      catchError(this.handleError),
      finalize(() => {
        this.redisFeatureFlagCacheRequest = null;
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );
  }

  /**
   *
   * @param response
   * @private
   */
  private getRedisFeatureFlagCacheDecision(response: unknown): boolean
  {
    const featureEntries = this.getMpopFeatureEntries(response);
    let isCacheEnabled = false;

    for (const featureEntry of featureEntries) {
      if (this.isMpopFeatureEntry(featureEntry)
        && featureEntry.name === UserService.redisFeatureFlagCacheFeatureName) {
        isCacheEnabled = featureEntry.enabled;
      }
    }

    return isCacheEnabled;
  }

  /**
   * Stores redisFeatureFlagCacheEnabled
   * @param isCacheEnabled
   * @private
   */
  private storeRedisFeatureFlagCacheDecision(isCacheEnabled: boolean): void
  {
    this.redisFeatureFlagCacheEnabled = isCacheEnabled;
    this.redisFeatureFlagCacheLoadedAt = Date.now();
  }

  /**
   * Checks if redisFeatureFlagCacheEnabled needs to be updated
   * @private
   */
  private isRedisFeatureFlagCacheDecisionCurrent(): boolean
  {
    let isCurrent = false;

    if (this.redisFeatureFlagCacheLoadedAt !== null) {
      const decisionAge = Date.now() - this.redisFeatureFlagCacheLoadedAt;
      isCurrent = decisionAge < UserService.featureSnapshotMaxAgeMilliseconds;
    }

    return isCurrent;
  }

  /**
   * Gets snapshot of mpop features
   * @private
   */
  private getMpopFeatureSnapshot(): Observable<Map<string, boolean>>
  {
    const featureSnapshot = this.mpopFeatureSnapshot;
    let featureRequest = this.mpopFeatureRequest;

    if (featureSnapshot !== null && this.isMpopFeatureSnapshotCurrent()) {
      featureRequest = of(featureSnapshot);
    }

    if (featureRequest === null) {
      featureRequest = this.loadMpopFeatureSnapshot();
      this.mpopFeatureRequest = featureRequest;
    }

    return featureRequest;
  }

  /**
   * Loads mpop feature snapshot
   * @private
   */
  private loadMpopFeatureSnapshot(): Observable<Map<string, boolean>>
  {

    return this.httpClient.get<unknown>(
      `//${environment.autoDomain}:${environment.autoPort}/api/mpop-feature`,
      this.publicEndpointOptions
    ).pipe(
      map((response) => this.buildMpopFeatureSnapshot(response)),
      tap((featureSnapshot) => this.storeMpopFeatureSnapshot(featureSnapshot)),
      catchError(this.handleError),
      finalize(() => {
        this.mpopFeatureRequest = null;
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );
  }

  /**
   * Builds mpop feature snapshot
   * @param response
   * @private
   */
  private buildMpopFeatureSnapshot(response: unknown): Map<string, boolean>
  {
    const featureSnapshot = new Map<string, boolean>();
    const featureEntries = this.getMpopFeatureEntries(response);

    for (const featureEntry of featureEntries) {
      if (this.isMpopFeatureEntry(featureEntry)) {
        featureSnapshot.set(featureEntry.name, featureEntry.enabled);
      }
    }

    return featureSnapshot;
  }

  /**
   * Gets entries in mpop feature
   * @param response
   * @private
   */
  private getMpopFeatureEntries(response: unknown): unknown[]
  {
    let featureEntries: unknown[] | null = null;

    if (Array.isArray(response)) {
      featureEntries = response;
    }

    if (featureEntries === null && this.isUnknownRecord(response)) {
      const hydraMembers = response['hydra:member'];
      if (Array.isArray(hydraMembers)) {
        featureEntries = hydraMembers;
      }
    }

    if (featureEntries === null) {
      throw new TypeError('Invalid MPOP feature response.');
    }

    return featureEntries;
  }

  /**
   * Checks if an entry is an mpop feature
   * @param featureEntry
   * @private
   */
  private isMpopFeatureEntry(featureEntry: unknown): featureEntry is MpopFeatureModel
  {
    let isFeatureEntry = false;

    if (this.isUnknownRecord(featureEntry)) {
      isFeatureEntry = typeof featureEntry['name'] === 'string'
        && featureEntry['name'].length > 0
        && typeof featureEntry['enabled'] === 'boolean';
    }

    return isFeatureEntry;
  }

  /**
   * verifies feature records
   * @param value
   * @private
   */
  private isUnknownRecord(value: unknown): value is Record<string, unknown>
  {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /**
   * Stores the snapshot
   * @param featureSnapshot
   * @private
   */
  private storeMpopFeatureSnapshot(featureSnapshot: Map<string, boolean>): void
  {
    this.mpopFeatureSnapshot = featureSnapshot;
    this.mpopFeatureSnapshotLoadedAt = Date.now();
  }

  /**
   * Checks if snapshot needs refresh
   * @private
   */
  private isMpopFeatureSnapshotCurrent(): boolean
  {
    let isCurrent = false;

    if (this.mpopFeatureSnapshotLoadedAt !== null) {
      const snapshotAge = Date.now() - this.mpopFeatureSnapshotLoadedAt;
      isCurrent = snapshotAge < UserService.featureSnapshotMaxAgeMilliseconds;
    }

    return isCurrent;
  }

  /**
   * Builds mpop feature results
   * @param featureSnapshot
   * @private
   */
  private buildMpopFeatureResults(featureSnapshot: Map<string, boolean>): MpopFeatureModel[]
  {
    const featureResults: MpopFeatureModel[] = [];

    featureSnapshot.forEach((enabled, name) => {
      featureResults.push(new MpopFeatureModel(name, enabled));
    });

    return featureResults;
  }

  public refreshSession(sessionRequest): Promise<any> {
    return firstValueFrom(this.httpClient.post(
      `//${environment.autoDomain}:${environment.autoPort}/api/refresh-session`,
      sessionRequest,
      this.postOptions
    ));
  }

  public userHasAccessKeys(user: UserInterface, accountId: number, requiredAccessKeys: string[]): boolean {
    let hasAccess = true;
    requiredAccessKeys.forEach((requiredAccessKey) => {
      if (!(accountId in user.permissions)
        || !(requiredAccessKey in user.permissions[accountId])
        || user.permissions[accountId][requiredAccessKey] === false) {
        hasAccess = false;
      }
      if ((isNaN(accountId) || accountId === undefined) && !hasAccess) {
        const keys = Object.keys(user.permissions);
        if (user.permissions[keys.shift()][requiredAccessKey] === true) {
          hasAccess = true;
        }
      }
    });

    return hasAccess;
  }

  public changePassword(changePasswordData): Observable<any> {
    return this.httpClient.patch(
      `//${environment.autoDomain}:${environment.autoPort}/api/users/password-change`,
      changePasswordData,
      this.patchOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  public resetPassword(resetPasswordData): Observable<any> {
    return this.httpClient.patch(
      `//${environment.autoDomain}:${environment.autoPort}/api/users/password-reset`,
      resetPasswordData,
      this.patchOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  public getResetCodeStatus(resetCode): Observable<any> {
    return this.httpClient.get(
      `//${environment.autoDomain}:${environment.autoPort}/api/users/reset-token/${resetCode}`,
      this.defaultOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Method to request a password reset
   *
   * @param username
   * @return Observable<any>
   */
  public requestResetPassword(username): Observable<any> {
    return this.httpClient.patch(
      `//${environment.autoDomain}:${environment.autoPort}/api/users/password-forgot-link`,
      username,
      this.patchOptions
    ).pipe(
      catchError(this.handleError)
    );
  }

  public logout(logoutRequest): Observable<any> {
    return this.httpClient.post(
      `//${environment.autoDomain}:${environment.autoPort}/api/logout`,
      logoutRequest,
      this.postOptions
    ).pipe(
      catchError(this.handleError)
    );
  }
}
